import { graphql } from '../graphql';

export const getAttachments = graphql(`
  query GetAttachments {
    attachments {
      id
      url
      s3Key
      uploadId
      filename
      contentType
      contentLength
      checksumSHA256Base64
      status
      createdAt
      updatedAt
      deletedAt
      organizationId
      projectId
    }
  }
`);

export const getAttachmentsByBatch = graphql(`
  query GetAttachmentsByBatch($batchId: ID!) {
    attachmentsByBatch(batchId: $batchId) {
      id
      url
      s3Key
      uploadId
      filename
      contentType
      contentLength
      checksumSHA256Base64
      status
      createdAt
      updatedAt
      organizationId
      projectId
    }
  }
`);

export const startUpload = graphql(`
  mutation StartUpload(
    $project: ID!
    $filename: String!
    $contentType: String!
    $checksumSHA256Base64: String
    $batch: ID
  ) {
    attachment: startUpload(
      project: $project
      filename: $filename
      contentType: $contentType
      checksumSHA256Base64: $checksumSHA256Base64
      batch: $batch
    ) {
      id
      url
      s3Key
      uploadId
      batchId
      filename
      contentType
      contentLength
      checksumSHA256Base64
      status
      createdAt
      updatedAt
      organizationId
      projectId
    }
  }
`);

export const startUploadBatch = graphql(`
  mutation StartUploadBatch($project: ID!, $numberOfAttachments: Int!, $label: String) {
    batch: startBatch(project: $project, numberOfAttachments: $numberOfAttachments, label: $label) {
      id
    }
  }
`);

export const completeUploadBatch = graphql(`
  mutation CompleteUploadBatch($id: ID!) {
    success: completeBatch(id: $id)
  }
`);

export const uploadPart = graphql(`
  mutation UploadPart($attachmentId: ID!, $partNumber: Int!, $contentLength: Int!, $checksumSHA256Base64: String!) {
    url: uploadPart(
      attachmentId: $attachmentId
      partNumber: $partNumber
      contentLength: $contentLength
      checksumSHA256Base64: $checksumSHA256Base64
    )
  }
`);

export const completeUpload = graphql(`
  mutation CompleteUpload($attachmentId: ID!, $parts: [S3CompletedPart!]!) {
    attachment: completeUpload(attachmentId: $attachmentId, parts: $parts) {
      id
      url
      s3Key
      uploadId
      filename
      contentType
      contentLength
      checksumSHA256Base64
      status
      createdAt
      updatedAt
      organizationId
      projectId
    }
  }
`);

export const abortUpload = graphql(`
  mutation AbortUpload($attachmentId: ID!) {
    success: abortUpload(attachmentId: $attachmentId)
  }
`);

export const startBatch = graphql(`
  mutation StartBatch($project: ID!, $numberOfAttachments: Int!, $label: String) {
    batch: startBatch(project: $project, numberOfAttachments: $numberOfAttachments, label: $label) {
      id
      status
      createdAt
      createdById
      numberOfAttachments
      processingStartedAt
      processingCompletedAt
      srid
    }
  }
`);

export const completeBatch = graphql(`
  mutation CompleteBatch($id: ID!) {
    success: completeBatch(id: $id)
  }
`);

export const getBatches = graphql(`
  query GetBatches($projectId: ID!) {
    batches(projectId: $projectId) {
      id
      key
      label
      kind
      status
      createdAt
      numberOfAttachments
      processingStartedAt
      processingCompletedAt
      srid
      createdBy {
        id
        name
        email
        avatar {
          id
          url
        }
      }
    }
  }
`);

export const getBatch = graphql(`
  query GetBatch($id: ID!) {
    batch(id: $id) {
      id
      key
      label
      kind
      status
      createdAt
      createdById
      createdBy {
        id
        name
        email
        avatar {
          id
          url
        }
      }
      srid
      numberOfAttachments
      processingStartedAt
      processingCompletedAt
      totalBytes
      attachments {
        id
        url
        s3Key
        filename
        contentType
        contentLength
        byproducts {
          id
          filename
          type
          contentLength
          downloadUrl
          expiresAt
          isInternal
        }
      }
      byproducts {
        id
        filename
        type
        contentLength
        downloadUrl
        expiresAt
        isInternal
        sourceAttachmentId
        processJobId
        createdAt
      }
      jobs {
        id
        type
        status
        inputJson
        outputJson
        leaseUntil
        attempts
        isLeaf
        batchId
        parentItemId
        label
        logs
        logStream
        errorLogs
        startedAt
        completedAt
        estimatedCompletion
        createdAt
        updatedAt
      }
    }
  }
`);

export const startBatchProcessing = graphql(`
  mutation StartBatchProcessing(
    $id: ID!
    $srid: Int!
    $type: ProcessJobType!
    $dxfOutputVersion: String
    $minZoom: Int
    $maxZoom: Int
    $tileSize: Int
    $pointCloudAttributes: [String!]
    $compressImages: Boolean
    $blurLicensePlates: Boolean
    $blurFaces: Boolean
    $generateRcs: Boolean
    $generate2dMap: Boolean
    $generateDecimatedPointCloud: Boolean
    $generateGeotiff: Boolean
    $importUrl: String
    $rasterMode: String
  ) {
    success: startBatchProcessing(
      id: $id
      srid: $srid
      type: $type
      dxfOutputVersion: $dxfOutputVersion
      minZoom: $minZoom
      maxZoom: $maxZoom
      tileSize: $tileSize
      pointCloudAttributes: $pointCloudAttributes
      compressImages: $compressImages
      blurLicensePlates: $blurLicensePlates
      blurFaces: $blurFaces
      generateRcs: $generateRcs
      generate2dMap: $generate2dMap
      generateDecimatedPointCloud: $generateDecimatedPointCloud
      generateGeotiff: $generateGeotiff
      importUrl: $importUrl
      rasterMode: $rasterMode
    )
  }
`);

export const createAttachment = graphql(`
  mutation CreateAttachment(
    $project: ID
    $filename: String!
    $contentType: String!
    $checksumSHA256Base64: String
    $type: UploadType!
  ) {
    createAttachment(
      project: $project
      filename: $filename
      contentType: $contentType
      checksumSHA256Base64: $checksumSHA256Base64
      type: $type
    ) {
      attachment {
        id
        url
        s3Key
        uploadId
        batchId
        filename
        contentType
        contentLength
        checksumSHA256Base64
        status
        createdAt
        updatedAt
        organizationId
        projectId
      }
      uploadUrl
    }
  }
`);

export const deleteAttachment = graphql(`
  mutation DeleteAttachment($attachmentId: ID!) {
    success: deleteAttachment(attachmentId: $attachmentId)
  }
`);

export const deleteBatchMutation = graphql(`
  mutation DeleteBatch($id: ID!) {
    success: deleteBatch(id: $id)
  }
`);

export const resetBatchMutation = graphql(`
  mutation ResetBatch($id: ID!) {
    success: resetBatch(id: $id)
  }
`);
